import type { Feature, Polygon } from 'geojson';
import * as R from 'ramda';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  AtomMap,
  grabFilter,
  Interaction,
  ItemWithLocation,
  latLngToUrlValue,
  urlValueToLatLng,
  useDefaultMapLocation,
} from '@atom/map';
import { MapIdleEvent, MapSearch, ViewState } from '@atom/types/map';

import { useMapPortalData } from './MapPortalDataProvider';
import MapPortalLayers from './MapPortalLayers';

const parseOffset = (value: string): { x: number; y: number } | null => {
  const [x, y = 0] = value.split(',').map(Number);

  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

const MapWrapper = () => {
  const { data, loading, setRegionFeature, setViewport } = useMapPortalData();
  const preferences = usePreferences();
  const defaultLocation = useDefaultMapLocation();

  const [
    { center, zoom, searchPoint, searchTerm, offset },
    { updateSearchParams },
  ] = useNavigation({
    center: SearchParamType.STRING,
    zoom: SearchParamType.NUMBER,
    searchPoint: SearchParamType.STRING,
    searchTerm: SearchParamType.STRING,
    view: SearchParamType.STRING,
    id: SearchParamType.STRING,
    hover: SearchParamType.STRING,
    offset: SearchParamType.STRING,
  });

  const urlCenter = center ? urlValueToLatLng(center) : null;
  const urlOffset = urlCenter && offset ? parseOffset(offset) : null;

  const initialViewState: ViewState = {
    center: urlCenter ?? defaultLocation.center,
    zoom: zoom || defaultLocation.zoom,
    searchPoint: searchPoint ? urlValueToLatLng(searchPoint) : undefined,
    searchTerm: searchTerm || undefined,
  };

  const handleIdle = (view: MapIdleEvent) => {
    setViewport(view);
    updateSearchParams({
      center: latLngToUrlValue(view.center),
      zoom: view.zoom,
      ...(offset ? { offset: null } : {}),
    });
  };

  const handleSearchChange = (search: MapSearch) =>
    updateSearchParams({
      searchPoint: search.searchPoint
        ? latLngToUrlValue(search.searchPoint)
        : null,
      searchTerm: search.searchTerm ?? null,
    });

  const createBulkWorkFromMap: boolean = R.pathOr(
    false,
    ['workOrders', 'createBulkWorkFromMap'],
    preferences,
  );

  const {
    clusters = [],
    workOrders = [],
    users = [],
    assets = [],
    assetsCount = 0,
    workOrdersCount = 0,
    usersCount = 0,
  } = data;
  const totalItems = assetsCount + workOrdersCount + usersCount;

  const handleGrab = (circle: Feature<Polygon>) => {
    const all: ItemWithLocation[] = [...assets, ...workOrders, ...users];

    const ids = grabFilter(all, circle);
    updateSearchParams({
      id: ids.length ? ids.join(',') : null,
      view: 'list',
      hover: null,
    });
  };

  const handleActiveFeatureChange = (feature: Feature | null) => {
    setRegionFeature(feature);

    if (feature !== null) {
      updateSearchParams({ view: 'list' });
    }
  };

  const handleDrawingModeChange = (_next: Interaction) => {
    updateSearchParams({
      id: null,
      ...(createBulkWorkFromMap ? { view: 'list' } : {}),
    });
  };

  return (
    <AtomMap
      initialViewState={initialViewState}
      onIdle={handleIdle}
      onSearchChange={handleSearchChange}
      recenter={
        urlOffset
          ? { center: urlCenter, active: true, zoom, offset: urlOffset }
          : undefined
      }
      loading={loading}
      drawing={{
        onChange: handleActiveFeatureChange,
        onGrab: handleGrab,
        onModeChange: handleDrawingModeChange,
        stats: {
          itemCounts: {
            total: totalItems,
            clusters: clusters.length,
            assets: assetsCount,
            workOrders: workOrdersCount,
            users: usersCount,
          },
          loading,
        },
      }}
    >
      <MapPortalLayers />
    </AtomMap>
  );
};

export default MapWrapper;
